import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, ShieldCheck, KeyRound, ArrowLeft } from 'lucide-react';
import { AuthLayout } from './AuthLayout';
import { OAuthButtons } from './OAuthButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { LoginSchema, type LoginValues } from './schemas';
import { login, loginTotp } from '@/api/auth';
import { applyServerErrors } from './parseApiError';

type Stage =
  | { kind: 'password' }
  | { kind: 'totp'; challenge: string };

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const [stage, setStage] = useState<Stage>({ kind: 'password' });
  const [showPw, setShowPw] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const dest = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

  const onPasswordSubmit = async (values: LoginValues) => {
    try {
      const result = await login(values);
      if (result.kind === 'mfa_required') {
        setStage({ kind: 'totp', challenge: result.challenge });
        return;
      }
      navigate(dest, { replace: true });
    } catch (err) {
      applyServerErrors(err, setError);
    }
  };

  return (
    <AuthLayout
      title={stage.kind === 'totp' ? 'Two-factor authentication' : 'Welcome back'}
      subtitle={
        stage.kind === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Sign in to your family account.'
      }
      footer={
        stage.kind === 'password' ? (
          <>
            New here?{' '}
            <Link to="/signup" className="font-medium text-[color:var(--kz-color-brand-primary)] hover:underline">
              Create an account
            </Link>
          </>
        ) : null
      }
    >
      {stage.kind === 'password' && (
        <>
          <OAuthButtons />

          <div className="my-6 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs uppercase tracking-wide text-[color:var(--kz-color-ink-muted)]">
              or with email
            </span>
            <Separator className="flex-1" />
          </div>

          <form onSubmit={handleSubmit(onPasswordSubmit)} noValidate className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email?.message && (
                <p className="text-xs text-[color:var(--kz-color-status-danger)]">{errors.email.message}</p>
              )}
            </div>

            <div className="grid gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[color:var(--kz-color-ink-secondary)] hover:text-[color:var(--kz-color-ink-primary)]"
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  {...register('password')}
                />
                <button
                  type="button"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--kz-color-ink-secondary)]"
                  onClick={() => setShowPw((v) => !v)}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password?.message && (
                <p className="text-xs text-[color:var(--kz-color-status-danger)]">{errors.password.message}</p>
              )}
            </div>

            {errors.root?.serverError?.message && (
              <p className="rounded-control border border-[color:var(--kz-color-status-danger)]/30 bg-[color:var(--kz-color-feedback-danger-bg)] p-3 text-sm text-[color:var(--kz-color-status-danger)]">
                {errors.root.serverError.message}
              </p>
            )}

            <Button type="submit" size="lg" disabled={isSubmitting} className="mt-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Signing in
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </>
      )}

      {stage.kind === 'totp' && (
        <TotpStep
          challenge={stage.challenge}
          onSuccess={() => navigate(dest, { replace: true })}
          onBack={() => setStage({ kind: 'password' })}
        />
      )}
    </AuthLayout>
  );
}

function TotpStep({
  challenge,
  onSuccess,
  onBack,
}: {
  challenge: string;
  onSuccess: () => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<'code' | 'backup'>('code');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const trimmed = value.trim();
      await loginTotp(
        mode === 'code'
          ? { challenge, code: trimmed }
          : { challenge, backup_code: trimmed },
      );
      onSuccess();
    } catch {
      setError(
        mode === 'code'
          ? 'That code is incorrect or expired. Try again.'
          : 'That backup code is invalid or already used.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-4">
      <div className="flex items-center gap-2 rounded-control border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-3 text-sm">
        <ShieldCheck className="size-4 shrink-0 text-emerald-600" />
        <p className="text-[color:var(--kz-color-ink-secondary)]">
          Your account is protected with two-factor authentication.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="totp-input">
          {mode === 'code' ? '6-digit code' : 'Backup code'}
        </Label>
        <Input
          id="totp-input"
          autoFocus
          inputMode={mode === 'code' ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          placeholder={mode === 'code' ? '123 456' : 'ABCDE-FGHIJ'}
          maxLength={mode === 'code' ? 8 : 16}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={mode === 'code' ? 'tracking-[0.5em] text-center text-lg' : 'text-center font-mono'}
        />
      </div>

      {error && (
        <p className="rounded-control border border-[color:var(--kz-color-status-danger)]/30 bg-[color:var(--kz-color-feedback-danger-bg)] p-3 text-sm text-[color:var(--kz-color-status-danger)]">
          {error}
        </p>
      )}

      <Button type="submit" size="lg" disabled={busy || !value.trim()} className="mt-1">
        {busy ? (
          <>
            <Loader2 className="animate-spin" />
            Verifying
          </>
        ) : (
          'Verify'
        )}
      </Button>

      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[color:var(--kz-color-ink-secondary)] hover:text-[color:var(--kz-color-ink-primary)]"
        >
          <ArrowLeft className="size-3.5" />
          Back to password
        </button>
        <button
          type="button"
          onClick={() => {
            setMode((m) => (m === 'code' ? 'backup' : 'code'));
            setValue('');
            setError(null);
          }}
          className="inline-flex items-center gap-1 font-medium text-[color:var(--kz-color-brand-primary)] hover:underline"
        >
          <KeyRound className="size-3.5" />
          {mode === 'code' ? 'Use a backup code' : 'Use authenticator code'}
        </button>
      </div>
    </form>
  );
}
